import { useState, useEffect } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Label,
  cn,
} from '@pantry/ui';
import {
  useCreateStorageLocation,
  useUpdateStorageLocation,
  EXPIRY_THRESHOLD_PRESETS,
  type StorageLocation,
  type StorageLocationType,
  STORAGE_LOCATION_TYPES,
} from '@pantry/api-client';
import { PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import { translatePresetLabel } from '@pantry/i18n';

interface StorageLocationFormDialogProps {
  householdId: string;
  location?: StorageLocation | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  householdExpiryThresholdDays?: number;
}

/**
 * Icon map for storage location types.
 */
const LOCATION_ICONS: Record<StorageLocationType, React.ReactNode> = {
  fridge: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <path d="M5 6a4 4 0 0 1 4-4h6a4 4 0 0 1 4 4v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6Z" />
      <path d="M5 10h14" />
      <path d="M15 7v6" />
    </svg>
  ),
  freezer: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <path d="M12 2v20" />
      <path d="M2 12h20" />
      <path d="m4.93 4.93 4.24 4.24" />
      <path d="m14.83 9.17 4.24-4.24" />
      <path d="m4.93 19.07 4.24-4.24" />
      <path d="m14.83 14.83 4.24 4.24" />
    </svg>
  ),
  pantry: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <path d="M4 10V4a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6" />
      <path d="M2 14h20" />
      <path d="M4 14v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-6" />
      <path d="M12 14v8" />
    </svg>
  ),
  other: (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
    >
      <rect width="18" height="18" x="3" y="3" rx="2" />
      <path d="M9 3v18" />
      <path d="M15 3v18" />
      <path d="M3 9h18" />
      <path d="M3 15h18" />
    </svg>
  ),
};

const getTypeLabels = (
  t: ReturnType<typeof useLingui>['t']
): Record<StorageLocationType, string> => ({
  fridge: t`Fridge`,
  freezer: t`Freezer`,
  pantry: t`Pantry`,
  other: t`Other`,
});

/**
 * Dialog for creating or editing a storage location.
 */
export function StorageLocationFormDialog({
  householdId,
  location,
  open,
  onOpenChange,
  onSuccess,
  householdExpiryThresholdDays = 3,
}: StorageLocationFormDialogProps) {
  const { t, i18n } = useLingui();
  const isEditing = !!location;
  const [name, setName] = useState('');
  const TYPE_LABELS = getTypeLabels(t);
  const [type, setType] = useState<StorageLocationType>('pantry');
  const [expiryThresholdDays, setExpiryThresholdDays] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const createLocation = useCreateStorageLocation();
  const updateLocation = useUpdateStorageLocation();

  // Reset form when dialog opens/closes or location changes
  useEffect(() => {
    if (open) {
      setName(location?.name || '');
      setType(location?.type || 'pantry');
      setExpiryThresholdDays(location?.expiryThresholdDays ?? null);
      setError(null);
    }
  }, [open, location]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError(t`Please enter a name for this location`);
      return;
    }

    try {
      if (isEditing && location) {
        await updateLocation.mutateAsync({
          householdId,
          id: location.id,
          data: { name: name.trim(), type, expiryThresholdDays },
        });
      } else {
        await createLocation.mutateAsync({
          householdId,
          data: { name: name.trim(), type, expiryThresholdDays },
        });
      }
      handleClose();
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to save storage location`;
      setError(message);
    }
  };

  const handleClose = () => {
    setName('');
    setType('pantry');
    setExpiryThresholdDays(null);
    setError(null);
    onOpenChange(false);
  };

  const isPending = createLocation.isPending || updateLocation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
              {isEditing ? (
                <PencilIcon className="h-5 w-5 text-primary" />
              ) : (
                <PlusIcon className="h-5 w-5 text-primary" />
              )}
            </div>
            {isEditing ? <Trans>Edit Location</Trans> : <Trans>Add Location</Trans>}
          </DialogTitle>
          <DialogDescription className="pt-1">
            {isEditing ? (
              <Trans>Update the details for this storage location.</Trans>
            ) : (
              <Trans>Add a new place to store your food items.</Trans>
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name input */}
          <div className="space-y-2">
            <Label htmlFor="location-name">
              <Trans>Name</Trans>
            </Label>
            <Input
              id="location-name"
              type="text"
              placeholder={t`e.g., Kitchen Fridge, Basement Freezer`}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-xl"
              autoFocus
            />
          </div>

          {/* Type selector */}
          <div className="space-y-2">
            <Label>
              <Trans>Type</Trans>
            </Label>
            <div className="grid grid-cols-2 gap-2">
              {STORAGE_LOCATION_TYPES.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setType(t)}
                  className={cn(
                    'flex items-center gap-3 rounded-xl border-2 p-3 transition-all duration-150',
                    type === t
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/30 hover:bg-muted/50'
                  )}
                >
                  <div
                    className={cn(
                      'flex h-9 w-9 items-center justify-center rounded-lg',
                      type === t ? 'bg-primary/10' : 'bg-muted'
                    )}
                  >
                    {LOCATION_ICONS[t]}
                  </div>
                  <span className="font-medium">{TYPE_LABELS[t]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Expiry Alert Threshold (optional) */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="location-expiry-threshold">
                <Trans>Expiry Alert Threshold (optional)</Trans>
              </Label>
              {expiryThresholdDays !== null && (
                <button
                  type="button"
                  onClick={() => setExpiryThresholdDays(null)}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                  aria-label={t`Clear threshold override`}
                >
                  <Trans>Clear</Trans>
                </button>
              )}
            </div>
            <select
              id="location-expiry-threshold"
              value={expiryThresholdDays ?? ''}
              onChange={(e) => {
                const val = e.target.value;
                setExpiryThresholdDays(val === '' ? null : parseInt(val, 10));
              }}
              aria-label={t`Expiry alert threshold`}
              className="flex h-10 w-full rounded-xl border border-border/50 bg-muted/30 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:border-primary/50 transition-all cursor-pointer hover:border-border disabled:cursor-not-allowed disabled:opacity-50"
            >
              <option value="">
                {t`Use household default (${householdExpiryThresholdDays} days)`}
              </option>
              {EXPIRY_THRESHOLD_PRESETS.map((preset) => (
                <option key={preset.days} value={preset.days}>
                  {translatePresetLabel(i18n, preset.label)}
                </option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground">
              <Trans>
                Override the household default for items in this location. Leave empty to use the
                household setting.
              </Trans>
            </p>
          </div>

          {error && (
            <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
              {error}
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
              className="rounded-xl"
            >
              <Trans>Cancel</Trans>
            </Button>
            <Button type="submit" disabled={isPending} className="rounded-xl">
              {isPending ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" />
                  {isEditing ? <Trans>Saving...</Trans> : <Trans>Creating...</Trans>}
                </div>
              ) : isEditing ? (
                <Trans>Save Changes</Trans>
              ) : (
                <Trans>Create Location</Trans>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
